import { Download, Eye, Pencil, Trash2, X } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button } from './Button'
import { StatusPill } from './StatusPill'

const statusValues = new Set([
  'Published',
  'Out of Stock',
  'Draft',
  'PAID',
  'PENDING',
  'PACKED',
  'PLACED',
  'CONFIRMED',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'SCHEDULED',
  'SENT',
  'Unread',
  'Read',
  'Active',
  'Inactive',
  'Blocked',
])

export function DataTable({
  title,
  columns,
  rows,
  hrefBase,
  editBase,
  editPath,
  viewPath,
  headerAction,
  hideHeaderActions = false,
  hideEditAction = false,
  hideDeleteAction = false,
  hidePagination = false,
  totalRows,
  page = 1,
  limit = 10,
  onPageChange,
  onLimitChange,
}: {
  title: string
  columns: string[]
  rows: string[][]
  hrefBase?: string
  editBase?: string
  editPath?: (rowIndex: number) => string
  viewPath?: (rowIndex: number) => string
  headerAction?: ReactNode
  hideHeaderActions?: boolean
  hideEditAction?: boolean
  hideDeleteAction?: boolean
  hidePagination?: boolean
  totalRows?: number
  page?: number
  limit?: number
  onPageChange?: (page: number) => void
  onLimitChange?: (limit: number) => void
}) {
  const [deleteRow, setDeleteRow] = useState<string | null>(null)
  const total = totalRows ?? rows.length
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const start = total === 0 ? 0 : (page - 1) * limit + 1
  const end = Math.min(total, page * limit)
  const pages = Array.from(new Set([
    1,
    Math.max(1, page - 1),
    page,
    Math.min(totalPages, page + 1),
    totalPages,
  ])).filter(item => item >= 1 && item <= totalPages)
  const hasActions = Boolean(hrefBase || editBase || editPath || !hideDeleteAction)

  return (
    <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 p-4">
        <h2 className="text-lg font-bold text-leaf-dark">{title}</h2>
        {!hideHeaderActions ? (
          <div className="flex gap-2">
            <Button icon={Download} variant="ghost">Export</Button>
            {headerAction}
          </div>
        ) : headerAction ? (
          <div className="flex gap-2">{headerAction}</div>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[840px] text-left text-sm">
          <thead className="bg-stone-50 text-xs uppercase text-stone-500">
            <tr>
              {columns.map((column) => (
                <th className="px-4 py-3 font-semibold" key={column}>{column}</th>
              ))}
              {hasActions ? <th className="px-4 py-3 font-semibold">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-center text-sm text-stone-500" colSpan={columns.length + (hasActions ? 1 : 0)}>
                  No records found
                </td>
              </tr>
            ) : null}
            {rows.map((row, rowIndex) => (
              <tr className="hover:bg-mango-yellow/10" key={row.join('-')}>
                {row.map((cell, index) => (
                  <td className="px-4 py-4 text-stone-700" key={`${cell}-${index}`}>
                    {index === 0 ? (
                      <span className="font-semibold text-leaf-dark">{cell}</span>
                    ) : statusValues.has(cell) ? (
                      <StatusPill value={cell} />
                    ) : (
                      cell
                    )}
                  </td>
                ))}
                {hasActions ? <td className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    {hrefBase ? (
                      <NavLink
                        className="grid h-9 w-9 place-items-center rounded-lg text-stone-500 hover:bg-stone-100 hover:text-leaf-dark"
                        to={viewPath ? viewPath(rowIndex) : `${hrefBase}/${rowIndex + 1}`}
                      >
                        <Eye className="h-4 w-4" />
                      </NavLink>
                    ) : (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-stone-500 hover:bg-stone-100" type="button">
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                    {!hideEditAction ? (
                      editPath || editBase ? (
                        <NavLink
                          className="grid h-9 w-9 place-items-center rounded-lg text-stone-500 hover:bg-stone-100 hover:text-leaf-dark"
                          to={editPath ? editPath(rowIndex) : `${editBase}/${rowIndex + 1}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </NavLink>
                      ) : (
                        <button className="grid h-9 w-9 place-items-center rounded-lg text-stone-500 hover:bg-stone-100" type="button">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )
                    ) : null}
                    {!hideDeleteAction ? (
                      <button
                        className="grid h-9 w-9 place-items-center rounded-lg text-red-500 hover:bg-red-50"
                        onClick={() => setDeleteRow(row[0])}
                        type="button"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hidePagination ? (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 px-4 py-3 text-sm text-stone-500">
        <span>Showing {start}-{end} of {total}</span>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-stone-500">
            Rows
            <select
              className="rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-mango-orange"
              onChange={(event) => onLimitChange?.(Number(event.target.value))}
              value={limit}
            >
              <option>10</option>
              <option>20</option>
              <option>50</option>
            </select>
          </label>
          <Button disabled={page <= 1} onClick={() => onPageChange?.(page - 1)} variant="ghost">Prev</Button>
          {pages.map((pageNumber, index) => (
            <Fragment key={pageNumber}>
            {index > 0 && pageNumber - pages[index - 1] > 1 ? <span className="px-1 text-stone-400" key={`gap-${pageNumber}`}>...</span> : null}
            <button
              className={pageNumber === page ? 'grid h-9 w-9 place-items-center rounded-lg bg-mango-orange text-sm font-bold text-white' : 'grid h-9 w-9 place-items-center rounded-lg border border-stone-200 bg-white text-sm font-semibold text-stone-600 hover:bg-stone-50'}
              onClick={() => onPageChange?.(pageNumber)}
              type="button"
            >
              {pageNumber}
            </button>
            </Fragment>
          ))}
          <Button disabled={page >= totalPages} onClick={() => onPageChange?.(page + 1)} variant="soft">Next</Button>
        </div>
      </div>
      ) : null}
      {deleteRow ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-stone-950/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-stone-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <div>
                  <h3 className="font-bold text-stone-950">Confirm delete</h3>
                  <p className="mt-1 text-sm leading-6 text-stone-600">
                    This will delete <span className="font-semibold text-stone-900">{deleteRow}</span>. This action will be audit logged.
                  </p>
                </div>
              </div>
              <button className="rounded-lg p-1 text-stone-400 hover:bg-stone-100" onClick={() => setDeleteRow(null)} type="button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setDeleteRow(null)} variant="ghost">Cancel</Button>
              <Button onClick={() => setDeleteRow(null)} variant="danger">Delete</Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
